import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { X, ChevronDown } from "lucide-react";

const Group = ({ label, items, value, onChange, testid }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const selected = value !== "all" ? value : null;
  return (
    <div className="mb-4 border-b border-[color:var(--c-line,rgba(0,0,0,0.08))] pb-4">
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left py-1 group"
      >
        <span className="flex flex-col items-start min-w-0">
          <span className="pv-label">{label}</span>
          {selected && !open && (
            <span data-testid={`${testid}-selected`} className="text-xs text-[color:var(--c-accent)] truncate max-w-[14rem] mt-1">{selected}</span>
          )}
        </span>
        <ChevronDown size={16} className={`flex-shrink-0 text-[color:var(--c-ink2)] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <ul className="space-y-1 mt-3" data-testid={testid}>
        <li>
          <button
            data-testid={`${testid}-all`}
            onClick={() => onChange("all")}
            className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${value === "all" ? "bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)]" : "hover:bg-[color:var(--c-surface)] text-[color:var(--c-ink)]"}`}
          >
            {t("all")}
          </button>
        </li>
        {items.map((it) => (
          <li key={it.name}>
            <button
              data-testid={`${testid}-${it.name}`}
              onClick={() => onChange(it.name)}
              className={`w-full flex items-start justify-between gap-3 px-3 py-1.5 text-sm text-left transition-colors ${value === it.name ? "bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)]" : "hover:bg-[color:var(--c-surface)] text-[color:var(--c-ink)]"}`}
            >
              <span className="text-left break-words min-w-0">{it.name}</span>
              <span className={`text-xs flex-shrink-0 ${value === it.name ? "text-[color:var(--c-on-dark)]" : "text-[color:var(--c-ink2)]"}`}>{it.count}</span>
            </button>
          </li>
        ))}
      </ul>
      )}
    </div>
  );
};

export const FilterSidebar = ({ facets, filters, setFilters, onAfterSelect }) => {
  const { t } = useApp();
  const active = filters.category !== "all" || filters.era !== "all" || filters.ad_language !== "all" || filters.brand !== "all";
  const apply = (patch) => { setFilters({ ...filters, ...patch }); onAfterSelect?.(); };

  return (
    <aside data-testid="filter-sidebar" className="w-full lg:w-72 flex-shrink-0 lg:sticky lg:top-24 h-fit lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto pv-scroll pr-1">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl">{t("filters")}</h2>
        {active && (
          <button
            data-testid="clear-filters"
            onClick={() => apply({ category: "all", era: "all", ad_language: "all", brand: "all" })}
            className="flex items-center gap-1 text-xs text-[color:var(--c-accent)] hover:underline"
          >
            <X size={13} /> {t("clear")}
          </button>
        )}
      </div>
      <Group label={t("topic")} items={facets.categories || []} value={filters.category}
        onChange={(v) => apply({ category: v })} testid="filter-topic" />
      <Group label={t("brands")} items={facets.brands || []} value={filters.brand}
        onChange={(v) => apply({ brand: v })} testid="filter-brand" />
      <Group label={t("era")} items={facets.eras || []} value={filters.era}
        onChange={(v) => apply({ era: v })} testid="filter-era" />
      <Group label={t("ad_language")} items={facets.ad_languages || []} value={filters.ad_language}
        onChange={(v) => apply({ ad_language: v })} testid="filter-adlang" />
    </aside>
  );
};
